/**
 * Auditoría: quién tocó qué.
 *
 * La versión anterior llamaba a /admin/audit-log, que no existió nunca — la
 * pantalla llevaba desde marzo devolviendo 404 en silencio. Esta usa el
 * endpoint real (GET /audit) creado en el rediseño.
 *
 * La versión posterior traía las 100 últimas entradas sin paginación, sin
 * filtros y sin avisar de que había más: la cola antigua del registro (lo de
 * mayo, cuando una Inspección lo pide en septiembre) dejaba de ser accesible
 * desde el producto con aspecto de «todo el registro» (ronda 3, r2b20[5]).
 * Ahora trae hasta FETCH_LIMIT (el backend admite 500), filtra por texto y
 * acción en cliente, acota por fecha pidiéndolo al endpoint, pagina y AVISA
 * cuando lo mostrado no es todo lo guardado.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { getAudit, AuditEntry } from '../../services/apiClient';

// El backend acota limit a 500 (audit.py); 300 es el corte que pedimos de
// serie para no arrastrar medio registro en cada apertura.
const FETCH_LIMIT = 300;
const PAGE_SIZE = 25;

export function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Cierto cuando el servidor nos dio exactamente el tope pedido: hay más
  // entradas fuera de este corte y hay que decirlo.
  const [truncated, setTruncated] = useState(false);

  const [textFilter, setTextFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async (from?: string, to?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAudit({
        limit: FETCH_LIMIT,
        dateFrom: from || undefined,
        dateTo: to || undefined,
      });
      setEntries(data);
      setTruncated(data.length >= FETCH_LIMIT);
      setPage(1);
    } catch {
      setError('No se pudo cargar la auditoría');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Texto libre y acción filtran EN CLIENTE sobre lo cargado (actor, acción,
  // recurso y resumen). Las fechas sí se piden al endpoint: es lo único que
  // permite llegar a lo de antes del corte cuando hay más de FETCH_LIMIT.
  const filtered = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    return entries.filter((e) => {
      if (actionFilter && e.action !== actionFilter) return false;
      if (!q) return true;
      const haystack = [
        e.actor_name,
        e.action,
        e.resource_type,
        e.resource_id != null ? String(e.resource_id) : '',
        e.summary ?? '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, textFilter, actionFilter]);

  const actions = useMemo(
    () => Array.from(new Set(entries.map((e) => e.action))).sort(),
    [entries],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageEntries = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  return (
    <AdminLayout title="Auditoría" subtitle="Solo lectura — un registro editable no es un registro">
      <div className="max-w-[860px]">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-3.5">{error}</p>}

        {/* Filtros */}
        <div className="rounded-2xl bg-surface border border-line p-4 mb-3.5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Buscar</span>
            <input
              className="field"
              placeholder="Persona, acción, recurso…"
              value={textFilter}
              onChange={(e) => { setTextFilter(e.target.value); setPage(1); }}
            />
          </label>
          <label className="block">
            <span className="label">Acción</span>
            <select
              className="field"
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            >
              <option value="">Todas</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label">Desde</span>
            <input
              className="field" type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <div className="flex items-end gap-2">
            <label className="block flex-1">
              <span className="label">Hasta</span>
              <input
                className="field" type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
            <button
              onClick={() => load(dateFrom, dateTo)}
              disabled={loading}
              className="h-[42px] px-4 rounded-xl bg-ink text-white font-semibold text-[13.5px] shrink-0 disabled:opacity-50"
            >
              {loading ? 'Cargando…' : 'Aplicar fechas'}
            </button>
          </div>
        </div>

        {truncated && (
          <p className="text-[12.5px] text-warn mb-3.5">
            Mostrando las últimas {entries.length} entradas{dateFrom || dateTo ? ' del rango pedido' : ''} —
            hay más en el registro. Acota por fecha para ver lo anterior.
          </p>
        )}

        <div className="rounded-2xl bg-surface border border-line overflow-hidden">
          {pageEntries.map((e) => (
            <div key={e.id} className="flex gap-3.5 px-5 py-3.5 border-b border-line last:border-b-0 items-start">
              <span className="font-mono text-[12px] text-ink-3 whitespace-nowrap pt-0.5 shrink-0">
                {new Date(e.timestamp).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <div className="min-w-0">
                <p className="text-[14.5px]">
                  <strong className="font-semibold">{e.actor_name}</strong>{' '}
                  <span className="text-ink-2">({e.actor_role.toLowerCase()})</span> · {e.action}
                </p>
                <p className="font-mono text-[12px] text-ink-2 mt-0.5 truncate">
                  {e.resource_type}
                  {e.resource_id != null && ` #${e.resource_id}`}
                  {e.summary && ` — ${e.summary}`}
                </p>
              </div>
            </div>
          ))}
          {entries.length === 0 && !error && !loading && (
            <p className="text-center text-[14px] text-ink-3 py-12">
              Sin actividad registrada {dateFrom || dateTo ? 'en ese rango de fechas' : 'todavía'}
            </p>
          )}
        </div>

        {/* Paginación en cliente */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3.5">
            <p className="text-[12.5px] text-ink-2">
              Mostrando {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="h-9 px-3.5 rounded-xl border border-line-strong font-semibold text-[13px] disabled:opacity-40"
              >
                ← Anterior
              </button>
              <span className="h-9 px-3 grid place-items-center font-mono text-[12.5px] text-ink-2">
                {safePage} / {totalPages}
              </span>
              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="h-9 px-3.5 rounded-xl border border-line-strong font-semibold text-[13px] disabled:opacity-40"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
